import { followUrlWithTracking } from '@avantia/client-and-server-utilities';
import { getConfig } from 'config/configuration';
import { AUDIT_TYPE } from 'interfaces/auditTypes';
import { sendAuditEvent } from 'utils';
import type { HpDispatchFunction } from './actionTypes';
import { fetchResolver } from './fetchResolver';

const { websiteEndpoint, policyPortalUrl, environment } = getConfig();
const policyPortalApiUrl = (() => {
  if (environment === 'developer') {
    const url = new URL(policyPortalUrl);
    url.port = String(parseInt(url.port, 10) + 1);
    return url.toString().replace(/\/$/, '');
  }

  return policyPortalUrl;
})();

export type PolicyPortalLandingContext = 'policy' | 'quote';

export interface RedirectToPolicyPortalProps {
  policyDetailsId: string;
  historyId: number;
  policyNumber?: string;
  questionSet: string;
  userName: string;
  isAgent: boolean;
  context: PolicyPortalLandingContext;
  emailAddress: string;
  postcode: string;
  onError?: () => void;
}

interface PolicyPortalRedirectApiResponse {
  key: string;
}

export const redirectToPolicyPortalAction = ({
  policyDetailsId,
  historyId,
  policyNumber,
  questionSet,
  userName,
  isAgent,
  context,
  emailAddress,
  postcode,
  onError
}: RedirectToPolicyPortalProps) => {
  return (dispatch: HpDispatchFunction<any>): Promise<void> | void => {
    const path = `/api/landing/${context}/policy-details-id/${policyDetailsId}/history-id/${historyId}/type/${questionSet}`;

    if (isAgent) {
      sendAuditEvent({
        activityId: AUDIT_TYPE.RENEWAL_JOURNEY_ACCESS_VALIDATION_SUCCESS,
        policyDetailsId,
        historyId,
        policyNumber
      });
      const queryParams = new URLSearchParams({ userName });
      followUrlWithTracking(`${policyPortalApiUrl}${path}?${queryParams.toString()}`, {
        context: { reason: 'policy-portal-redirect-agent' }
      });
      dispatch({ type: 'POLICY_PORTAL_REDIRECT_SUCCESS' });
      return;
    }

    dispatch({ type: 'POLICY_PORTAL_REDIRECT_LOADING' });

    return fetchResolver<PolicyPortalRedirectApiResponse>({
      options: {
        url: `${websiteEndpoint}/api/policy-portal-redirect`,
        method: 'POST',
        data: { policyDetailsId, historyId, questionSet, emailAddress, postcode },
        description: 'Redirect to Policy Portal'
      },
      dispatch,
      successAction: (response) => {
        sendAuditEvent({
          activityId: AUDIT_TYPE.RENEWAL_JOURNEY_ACCESS_VALIDATION_SUCCESS,
          policyDetailsId,
          historyId,
          policyNumber
        });
        const queryParams = new URLSearchParams({
          userName,
          customerDetailsKey: response.key
        });
        followUrlWithTracking(`${policyPortalApiUrl}${path}?${queryParams.toString()}`, {
          context: { reason: 'policy-portal-redirect-customer' }
        });
        dispatch({ type: 'POLICY_PORTAL_REDIRECT_SUCCESS' });
      },
      failedAction: () => {
        dispatch({ type: 'POLICY_PORTAL_REDIRECT_ERROR' });
        onError?.();
      }
    });
  };
};
